import { useQuery } from "@tanstack/react-query";
import { withNamespaces } from "react-i18next";
import { RiBuilding4Fill, RiUser3Fill } from "react-icons/ri";
import { toast } from "react-toastify";
import { Card, CardBody, CardFooter, CardTitle, Progress } from "reactstrap";
import ComplianceMonitoringDashboardService from "src/modules/3rd-party-management/apis/admin/compliance/monitoring/DashboardService";

const Categories = ({ t, filters }) => {
    const fetchDataQuery = useQuery({
        queryKey: [
            "3rd-party-management-fetch-compliance-monitoring-dashboard",
            "categories",
            filters
        ],
        queryFn: async () => {
            const service = ComplianceMonitoringDashboardService.getInstance();

            return await service.fetchCategories(filters);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        retry: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching dashboard data."), {
                type: "error"
            });
        }
    });

    if (fetchDataQuery.isFetching) {
        return (
            <Card>
                <CardBody>
                    <div className="dt-field dt-skeleton mb-2" style={{ width: '20%' }}></div>
                    <div className="dt-field dt-skeleton" style={{ width: '100%', height: '320px' }}></div>
                </CardBody>
            </Card>
        );
    }

    const {
        companies = 0,
        individuals = 0,
        total = 0
    } = fetchDataQuery.data || {};

    const companiesPercentage = total > 0 ? (companies / total) * 100 : 0;
    const individualsPercentage = total > 0 ? (individuals / total) * 100 : 0;

    return (
        <Card className="h-100 categories-card">
            <CardBody>
                <CardTitle>
                    {t('Categories')}
                </CardTitle>

                <div className="categories-list d-flex flex-column gap-2">
                    <div className="category-item d-flex">
                        <div className="left-section d-flex align-items-center justify-content-between gap-2">
                            <div className="icon individuals">
                                <RiUser3Fill size="24" />
                            </div>
                            <div>
                                <p className="mb-0 font-size-14">
                                    {t('Individuals')}
                                </p>
                                <span>{individuals}</span>
                            </div>
                        </div>

                        <div className="right-section w-100">
                            <div className="d-flex flex-column align-items-center">
                                <small>
                                    {individualsPercentage.toFixed(2)}% {t('of total')}
                                </small>
                                <div className="w-100">
                                    <Progress 
                                        barClassName="individuals-progress" 
                                        value={individualsPercentage} 
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="category-item d-flex">
                        <div className="left-section d-flex align-items-center justify-content-between gap-2">
                            <div className="icon companies">
                                <RiBuilding4Fill size="24" />
                            </div>
                            <div>
                                <p className="mb-0 font-size-14">
                                    {t('Companies')}
                                </p>
                                <span>
                                    {companies}
                                </span>
                            </div>
                        </div>

                        <div className="right-section w-100">
                            <div className="d-flex flex-column align-items-center">
                                <small>
                                    {companiesPercentage.toFixed(2)}% of total
                                </small>
                                <div className="w-100">
                                    <Progress 
                                        value={companiesPercentage} 
                                        barClassName="companies-progress"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </CardBody>

            <CardFooter
                    className="bg-transparent d-flex justify-content-between"
                >
                    <span className="fw-bold">
                        {t('Total')}
                    </span>

                    <span className="fw-bold">
                        {total}
                    </span>
                </CardFooter>
        </Card>
    )
}

export default withNamespaces()(
    Categories
);